//#region imports

import { Box, FormControl, Button, FormControlLabel, Grid, Radio, RadioGroup, TextField, Typography, Autocomplete } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Yup from "yup";
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DoneIcon from '@mui/icons-material/Done';
import { cfpBaseUrl } from '../../../network/urls';
import "./OptForSmartMeterForm.scss";
import { CircularProgress } from '@mui/material';
import { documentExtensions, portalName } from '../../../utils/PortalName';
import { dBillAPI } from '../../../services/Functions';
import { useSelector } from 'react-redux';

//#endregion

const OptForSmartMeterForm = () => {
  //#region initial values
  const initialValues = {
    // Classification group Initial Values
    iv_reference_no: '',
    iv_circle: '',
    iv_division: '',
    iv_sub_divison: '',
    iv_address: '',
    // Payment plan field
    iv_payment_plan: 1,
    // Applicant Details group Initial Values
    iv_applicant_name: '',
    iv_applicant_cnic: '',
    iv_applicant_phone_no: '',
    iv_applicant_email: '',
    // Required Documents Initial Values
    iv_applicant_cnic_document: null,
    iv_last_paid_bill: null,
  }
  //#endregion

  //#region states
  // Classification Group States
  const [referenceNumber, setReferenceNumber] = useState('');
  const [subDivision, setSubDivision] = React.useState({ code: null, name: '' });
  const [address, setAddress] = useState('');
  const [paymentPlan, setPaymentPlan] = useState(1);
  // Documents states
  const [applicantCnicDocument, setApplicantCnicDocument] = useState('');
  const [lastPaidBill, setLastPaidBill] = useState('');
  // Thumbnail states
  const [applicantCnicDocumentThumbnail, setApplicantCnicDocumentThumbnail] = useState(null);
  const [lastPaidBillThumbnail, setLastPaidBillThumbnail] = useState(null);
  // States for hiding conditional fields
  const [citiesLists, setCitiesLists] = useState([]);
  const [hierarchyList, setHierarchyList] = useState([]);
  const [isLoadingSpinner, setIsLoadingSpinner] = React.useState(false);

  const [circleList, setCircleList] = useState([]);
  const [divisionList, setDivisionList] = useState([]);
  const [subDivisionList, setSubDivisionList] = useState([]);
  const [defaultCircle, setDefaultCircle] = useState();
  const [defaultDivision, setDefaultDivision] = useState();
  //#endregion

  //#region local storage
  const { token, code: userCode,roleTypeId } = useSelector((state) => state.auth)
  let userCompany = userCode.slice(0, 2);
  let userCircle = userCode.slice(0, 3);
  let userDivision = userCode.slice(0, 4);
  //#endregion

  //#region formik
  const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues,
      validationSchema: Yup.object().shape({
        // Classification group Validations
        iv_reference_no: Yup.string().min(14, "Please enter 14 digit reference number.").max(14, "Please enter 14 digit reference number.").matches(/^[0-9]+$/, "Enter 14 digit reference number as 01234567891234").required('Enter reference number for smart meter application.'),
        iv_circle: Yup.mixed().required("Please select a circle."),
        iv_division: Yup.mixed().required("Please select a division."),
        iv_sub_divison: Yup.mixed().required("Please select a sub division."),
        iv_payment_plan: Yup.mixed().required("Please select a payment plan."),
        // Applicant Details group Validations
        iv_applicant_name: Yup.string().min(3, "Name should be atleast 3 characters.").max(30, "Name should be less than 30 characters.").required("Please enter applicant name."),
        iv_applicant_cnic: Yup.string().min(13, "Please enter 13 digit CNIC.").max(13, "Please enter 13 digit CNIC.").required("Please enter valid CNIC number.").matches(/^[0-9]+$/, "Enter CNIC number as 3520212345678."),
        iv_applicant_phone_no: Yup.string().max(11, "Please enter 11 digit phone number.").min(11, "Please enter 11 digit phone number.").required("Please enter valid mobile number.").matches(/^03[0-9]{2}[0-9]{7}$/, "Enter Phone number as 03001234567"),
        iv_applicant_email: Yup.string().email("Enter email as 'john@gmail.com'.").notRequired(),
        // Required Documents Validation
        iv_applicant_cnic_document: Yup.mixed().required('Please upload cnic in jpg, jpeg, png or pdf format.'),
        iv_last_paid_bill: Yup.mixed().required('Please upload last paid bill in jpg, jpeg, png or pdf format.'),
      }),
      onSubmit: (values, { resetForm }) => {
        setIsLoadingSpinner(true);
        var formdata = new FormData();


        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);;
        formdata.append("createdBy", window.localStorage.getItem('user-name'));
        formdata.append("assignedTo", "");
        formdata.append("approvedBy", "");
        formdata.append("applicationStatusId", 1);
        formdata.append("lastModifiedBy", "");
        formdata.append("remarks", "");
        formdata.append("remarksDetail", "");
        formdata.append("isOnline", 1);
        formdata.append("submittedThrough", portalName);
        formdata.append("subDivisionCode", subDivision.code);
        formdata.append("divisionCode", defaultDivision.code);
        formdata.append("circleCode", defaultCircle.code);
        formdata.append("companyCode", parseInt(userCompany));
        formdata.append("withDrawlStatus", "");
        formdata.append("termAndConditionFlag", "yes");
        formdata.append("refNo", values.iv_reference_no);
        formdata.append("installmentPlan", values.iv_payment_plan);
        formdata.append("applicantName", values.iv_applicant_name);
        formdata.append("applicantCNIC", values.iv_applicant_cnic);
        formdata.append("applicantPhone", values.iv_applicant_phone_no);
        formdata.append("applicantEmail", values.iv_applicant_email);
        formdata.append("applicantAddress", address);
        formdata.append("copyOfApplicantCNIC", applicantCnicDocumentThumbnail);
        formdata.append("applicantLastPaidBill", lastPaidBillThumbnail);
        formdata.append("applicationProgressId", 1);
        formdata.append("cfcId", userCode);
        var requestOptions = {
          method: 'POST',
          body: formdata,
          headers: myHeaders,
          redirect: 'follow',
        };
        fetch(cfpBaseUrl + "optForSmartMeter/opt", requestOptions)
          .then(response => response.json())
          .then(result => {
            const msg = result.message;
            if (result.status === '200') {
              const applicationID = result.applicationId
              // toast.success(msg + "\n Tracking Id is: " + applicationID)
              setIsLoadingSpinner(false)
              setPaymentPlan(1)
              setDefaultCircle()
              setDefaultDivision()
              setSubDivision()
              setAddress('')
              setApplicantCnicDocument()
              setApplicantCnicDocumentThumbnail()
              setLastPaidBill()
              setLastPaidBillThumbnail()
              resetForm()
            }
            else {
              setIsLoadingSpinner(false)
            }
          })
          .catch(error => {
            setIsLoadingSpinner(false)
          });
      }
    })
  //#endregion

  //#region use effects
  useEffect(() => {
    if (userCompany) {
      var formdata = new FormData();
      if (parseInt(userCompany) !== 11 && parseInt(userCompany) !== 12 && parseInt(userCompany) !== 13 && parseInt(userCompany) !== 15 && parseInt(userCompany) !== 26 && parseInt(userCompany) !== 37 && parseInt(userCompany) !== 38 && parseInt(userCompany) !== 48 && parseInt(userCompany) !== 59) {
        formdata.append("companyCode", 15);
      } else {
        formdata.append("companyCode", parseInt(userCompany));
      }
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);;
      var requestOptions = {
        method: 'POST',
        body: formdata,
        headers: myHeaders,
        redirect: 'follow',
      };

      fetch(cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails", requestOptions)
        .then(response => response.json())
        .then(result => {
          let hierarchyArray = result.message
          let sortedSdArray = hierarchyArray.sort(function (a, b) {
            var sdA = a.subDivisionName.toUpperCase(); // convert to uppercase to ignore case sensitivity
            var sdB = b.subDivisionName.toUpperCase(); // convert to uppercase to ignore case sensitivity
            if (sdA < sdB) {
              return -1; // nameA comes before nameB in the sorted order
            }
            if (sdA > sdB) {
              return 1; // nameA comes after nameB in the sorted order
            }
            return 0; // names are equal, their order doesn't matter
          });

          setHierarchyList(sortedSdArray)

          let uniqueCircles = []

          for (let i = 0; i < sortedSdArray.length; i++) {
            const circle = sortedSdArray[i];
            const existingCircle = uniqueCircles.find(c => c.code === circle.circleCode);
            if (!existingCircle) {
              uniqueCircles.push({
                code: circle.circleCode,
                name: circle.circleName
              });
            }
          }

          let sortedCircleArray = uniqueCircles.sort(function (a, b) {
            var circleA = a.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
            var circleB = b.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
            if (circleA < circleB) {
              return -1; // nameA comes before nameB in the sorted order
            }
            if (circleA > circleB) {
              return 1; // nameA comes after nameB in the sorted order
            }
            return 0; // names are equal, their order doesn't matter
          });

          let defCircle = sortedCircleArray.find(item => item.code === parseInt(userCircle))

          // setDefaultCircle(defCircle)
          //   values.iv_circle = defCircle.code

          setCircleList(sortedCircleArray)

          let uniqueDivisions = []

          for (let i = 0; i < sortedSdArray.length; i++) {
            const division = sortedSdArray[i];
            if (division.circleCode === parseInt(userCircle)) {
              const existingDivision = uniqueDivisions.find(c => c.code === division.divisionCode);
              if (!existingDivision) {
                uniqueDivisions.push({
                  code: division.divisionCode,
                  name: division.divisionName
                });
              }
            }
          }

          let sortedDivisionArray = uniqueDivisions.sort(function (a, b) {
            var divisionA = a.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
            var divisionB = b.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
            if (divisionA < divisionB) {
              return -1; // nameA comes before nameB in the sorted order
            }
            if (divisionA > divisionB) {
              return 1; // nameA comes after nameB in the sorted order
            }
            return 0; // names are equal, their order doesn't matter
          });
          let defDivision = sortedDivisionArray.find(item => item.code === parseInt(userDivision))

          //  setDefaultDivision(defDivision)
          // values.iv_division = defDivision.code

          setDivisionList(sortedDivisionArray)

          let uniqueSubDivisions = []

          for (let i = 0; i < sortedSdArray.length; i++) {
            const subDivision = sortedSdArray[i];
            if (subDivision.divisionCode === parseInt(userDivision)) {
              const existingSubDivision = uniqueSubDivisions.find(c => c.code === subDivision.subDivisionCode);
              if (!existingSubDivision) {
                uniqueSubDivisions.push({
                  code: subDivision.subDivisionCode,
                  name: subDivision.subDivisionName
                });
              }
            }
          }

          let sortedSubDivisionArray = uniqueSubDivisions.sort(function (a, b) {
            var subDivisionA = a.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
            var subDivisionB = b.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
            if (subDivisionA < subDivisionB) {
              return -1; // nameA comes before nameB in the sorted order
            }
            if (subDivisionA > subDivisionB) {
              return 1; // nameA comes after nameB in the sorted order
            }
            return 0; // names are equal, their order doesn't matter
          });
          setSubDivisionList(sortedSubDivisionArray)
        }
        )
        .catch(error => console.log('error', error));
    }
  }, [])

  useEffect(() => {
    if (defaultCircle) {

      if (hierarchyList) {
        let hierarchyArray = hierarchyList
        let uniqueDivisions = []

        for (let i = 0; i < hierarchyArray.length; i++) {
          const division = hierarchyArray[i];
          if (division.circleCode === defaultCircle.code) {
            const existingDivision = uniqueDivisions.find(c => c.code === division.divisionCode);
            if (!existingDivision) {
              uniqueDivisions.push({
                code: division.divisionCode,
                name: division.divisionName
              });
            }
          }
        }

        let sortedDivisionArray = uniqueDivisions.sort(function (a, b) {
          var divisionA = a.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
          var divisionB = b.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
          if (divisionA < divisionB) {
            return -1; // nameA comes before nameB in the sorted order
          }
          if (divisionA > divisionB) {
            return 1; // nameA comes after nameB in the sorted order
          }
          return 0; // names are equal, their order doesn't matter
        });
        setDivisionList(sortedDivisionArray)
      }
    }
  }, [defaultCircle])

  useEffect(() => {
    if (defaultDivision) {

      if (hierarchyList) {
        let hierarchyArray = hierarchyList
        let uniqueSubDivisions = []
        for (let i = 0; i < hierarchyArray.length; i++) {
          const subDivision = hierarchyArray[i];
          if (defaultDivision != null) {
            if (subDivision.divisionCode === defaultDivision.code) {
              const existingSubDivision = uniqueSubDivisions.find(c => c.code === subDivision.subDivisionCode);
              if (!existingSubDivision) {
                uniqueSubDivisions.push({
                  code: subDivision.subDivisionCode,
                  name: subDivision.subDivisionName
                });
              }
            }
          }
        }
        let sortedSubDivisionArray = uniqueSubDivisions.sort(function (a, b) {
          var subDivisionA = a.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
          var subDivisionB = b.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
          if (subDivisionA < subDivisionB) {
            return -1; // nameA comes before nameB in the sorted order
          }
          if (subDivisionA > subDivisionB) {
            return 1; // nameA comes after nameB in the sorted order
          }
          return 0; // names are equal, their order doesn't matter
        });
        setSubDivisionList(sortedSubDivisionArray)
      }
    }
  }, [defaultDivision])

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetch(cfpBaseUrl + 'electricityNewConnection/citiesList');
      const data = await response.json();
      setCitiesLists(data);
    };
    if(roleTypeId == 1){
      fetchData();
    }
  }, []);

  // Calling Billing API to fetch near by details
  useEffect(() => {
    if (referenceNumber.length === 14) {
      let enteredReferenceNo = referenceNumber
      let circleCode = enteredReferenceNo.slice(2, 5)
      let circleObject = circleList.find(item => item.code === parseInt(circleCode))
      setDefaultCircle(circleObject)
      values.iv_circle = circleObject.code

      let divisionCode = enteredReferenceNo.slice(2, 6)
      let subDivisionCode = enteredReferenceNo.slice(2, 7)

      let uniqueDivisions = []

      for (let i = 0; i < hierarchyList.length; i++) {
        const division = hierarchyList[i];
        if (division.circleCode === parseInt(circleCode)) {
          const existingDivision = uniqueDivisions.find(c => c.code === division.divisionCode);
          if (!existingDivision) {
            uniqueDivisions.push({
              code: division.divisionCode,
              name: division.divisionName
            });
          }
        }
      }

      let sortedDivisionArray = uniqueDivisions.sort(function (a, b) {
        var divisionA = a.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
        var divisionB = b.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
        if (divisionA < divisionB) {
          return -1; // nameA comes before nameB in the sorted order
        }
        if (divisionA > divisionB) {
          return 1; // nameA comes after nameB in the sorted order
        }
        return 0; // names are equal, their order doesn't matter
      });
      let defDivision = sortedDivisionArray.find(item => item.code === parseInt(divisionCode))

      setDefaultDivision(defDivision)
      values.iv_division = defDivision.code
      setDivisionList(sortedDivisionArray)

      let uniqueSubDivisions = []

      for (let i = 0; i < hierarchyList.length; i++) {
        const subDivision = hierarchyList[i];
        if (subDivision.divisionCode === parseInt(divisionCode)) {
          const existingSubDivision = uniqueSubDivisions.find(c => c.code === subDivision.subDivisionCode);
          if (!existingSubDivision) {
            uniqueSubDivisions.push({
              code: subDivision.subDivisionCode,
              name: subDivision.subDivisionName
            });
          }
        }
      }

      let sortedSubDivisionArray = uniqueSubDivisions.sort(function (a, b) {
        var subDivisionA = a.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
        var subDivisionB = b.name.toUpperCase(); // convert to uppercase to ignore case sensitivity
        if (subDivisionA < subDivisionB) {
          return -1; // nameA comes before nameB in the sorted order
        }
        if (subDivisionA > subDivisionB) {
          return 1; // nameA comes after nameB in the sorted order
        }
        return 0; // names are equal, their order doesn't matter
      });
      // let divisionObject = sortedDivisionArray.find(item => item.code === parseInt(divisionCode))
      // setDefaultDivision(divisionObject)
      let subDivisionObject = sortedSubDivisionArray.find(item => item.code === parseInt(subDivisionCode))
      values.iv_sub_divison = subDivisionObject.code
      setSubDivision(subDivisionObject)
      setSubDivisionList(sortedSubDivisionArray)
      referenceApi();
    }
  }, [referenceNumber])

  //#endregion

  //#region API functions
  // Function to fetch sub division and near by name and address through API 
  const referenceApi = async () => {
    if (!referenceNumber) return;
    let apiResponse = await dBillAPI(token, referenceNumber)
    if (apiResponse.status) {
      const basicInfo = apiResponse.basicInfo
      setAddress(basicInfo.consumerAddress1 + basicInfo.consumerAddress2)
    } else {
      console.log("Reference number doesn't exists.")
    }
  };

  const PaymentPlan = (event) => {
    setPaymentPlan(event.target.value);
    values.iv_payment_plan = event.target.value;
  };

  //#endregion

  //#region functions
  const ReferenceNumber = event => {
    handleChange(event)
    if (event.target.value.length === 14) {
      setReferenceNumber(event.target.value);
    }
  };

  const Address = event => {
    values.iv_address = event.target.value
  };

  //Function to set states of documents
  const ApplicantCnicDocument = (event) => {
    handleChange(event);
    if (event.target.files.length > 0) {
      let file = event.target.files[0];
      let fileName = file.name
      let fileNameArray = fileName.split('.')
      let extension = fileNameArray[fileNameArray.length - 1]
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      const documentValidate = documentValidation(file, maxSize)
      if (documentValidate === 1) {
        const newFileName = "copyOfApplicantCNIC." + extension;
        const renamedFile = new File([file], newFileName, { type: file.type });
        setApplicantCnicDocument(renamedFile);
        const reader = new FileReader();

        reader.onload = (e) => {
          // Set the thumbnail image as the data URL
          setApplicantCnicDocumentThumbnail(e.target.result);
        };

        reader.readAsDataURL(file);
      } else if (documentValidate === 2) {
        console.log("File size exceeds " + maxSize / 1024 / 1024 + "MB limit.");
      } else {
        console.log("Invalid file type. Only JPG, PNG, and PDF are allowed.");
      }
    }
  }

  const LastPaidBill = (event) => {
    handleChange(event);
    if (event.target.files.length > 0) {
      let file = event.target.files[0];
      let fileName = file.name
      let fileNameArray = fileName.split('.')
      let extension = fileNameArray[fileNameArray.length - 1]
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      const documentValidate = documentValidation(file, maxSize)
      if (documentValidate === 1) {
        const newFileName = "lastPaidBill." + extension;
        const renamedFile = new File([file], newFileName, { type: file.type });
        setLastPaidBill(renamedFile);
        const reader = new FileReader();

        reader.onload = (e) => {
          // Set the thumbnail image as the data URL
          setLastPaidBillThumbnail(e.target.result);
        };

        reader.readAsDataURL(file);
      } else if (documentValidate === 2) {
        console.log("File size exceeds " + maxSize / 1024 / 1024 + "MB limit.");
      } else {
        console.log("Invalid file type. Only JPG, PNG, and PDF are allowed.");
      }
    }
  }
  const documentValidation = (file, maxSize) => {
    let fileExtension = file.name.split(".").pop();

    if (["jpg", "png", "pdf", "JPG", "PNG", "jpeg", "JPEG", "PDF"].includes(fileExtension.toLowerCase())) {
      if (file.size <= maxSize) {
        return 1
      } else {
        return 2
      }
    } else {
      return 3
    }
  }

  //#endregion

  return (
    <Grid container className='opt'>
      <Box className='optContainer'>
        <Typography variant='h6' className='heading'>
          Opt For Smart Meter Form
        </Typography>
        <Box className="progressBarBackground" >
          <Box className="progressBarContainer">
            <Box className="pbDocGroup">
              <Typography className="badge">
                {
                  applicantCnicDocument
                    ?
                    <Box className='backgroundAttached'>
                      <DoneIcon className='icon' />
                    </Box> :
                    <p className='backgroundUnAttached'>1</p>
                }
              </Typography>
              <Typography className='badgeText'>Applicant's CNIC.</Typography>
            </Box>
            <Box className="pbDocGroup">
              <Typography className="badge">
                {
                  lastPaidBill
                    ?
                    <Box className='backgroundAttached'>
                      <DoneIcon className='icon' />
                    </Box> :
                    <p className='backgroundUnAttached'>2</p>
                }
              </Typography>
              <Typography className='badgeText'>Last Paid Bill</Typography>
            </Box>
          </Box>
        </Box>
        <form className='ncform'>
          <Typography variant="h6" className='groupTitle' >
            Application Details
          </Typography>
          <Grid container>
            <Grid item xs={4}>
              <Typography className='fieldLabel' >Reference No.</Typography>
              <TextField
                type='text'
                name="iv_reference_no"
                autoComplete='off'
                value={values.iv_reference_no}
                onChange={ReferenceNumber}
                onBlur={handleBlur}
                inputProps={{ maxLength: 14 }}
                id="iv_reference_no"
                className='textInputField'
              />
              <Box className='from-Error'>
                {errors.iv_reference_no && touched.iv_reference_no ? (errors.iv_reference_no) : null}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography className='fieldLabel' >Circle</Typography>
              <Autocomplete
                id="iv_circle"
                name="iv_circle"
                disabled
                value={defaultCircle ? defaultCircle : null}
                options={circleList}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" className='textInputFieldAutoSelect' />
                )}
                onChange={(event, value) => {
                  if (value) {
                    setDefaultCircle(value);
                    values.iv_circle = value.code
                    // setDefaultDivision();
                    // setSubDivision();
                  }
                }}
              />
              <Box className='from-Error'>
                {errors.iv_circle && touched.iv_circle ? (errors.iv_circle) : null}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography className='fieldLabel' >Division</Typography>
              <Autocomplete
                id="iv_division"
                name="iv_division"
                disabled
                options={divisionList}
                value={defaultDivision ? defaultDivision : null}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" className='textInputFieldAutoSelect' />
                )}
                onChange={(event, value) => {
                  if (value) {
                    setDefaultDivision(value);
                    values.iv_division = value.code
                    // setSubDivision()
                  }
                }}
              />
              <Box className='from-Error'>
                {errors.iv_division && touched.iv_division ? (errors.iv_division) : null}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography className='fieldLabel' >Sub Division</Typography>
              <Autocomplete
                id="iv_sub_divison"
                value={subDivision ? subDivision : null}
                name="iv_sub_divison"
                disabled
                options={subDivisionList}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" className='textInputFieldAutoSelect' />
                )}
                onChange={(event, value) => {
                  if (value) {
                    setSubDivision(value);
                    values.iv_sub_divison = value.code
                  }
                }}
              />
              <Box className='from-Error'>
                {errors.iv_sub_divison && touched.iv_sub_divison ? (errors.iv_sub_divison) : null}
              </Box>
            </Grid>
            <Grid item xs={8}>
              <Typography className='fieldLabelNameAndAddressing'>Address</Typography>
              <TextField name='NearByNameAndAddress'
                required
                type='text'
                onChange={Address}
                onBlur={handleBlur}
                value={address}
                disabled
                className='textInputFieldNameAndAddressing' />
            </Grid>
          </Grid>
          <Typography variant="h6" className='groupTitle'>
            Payment Plan
          </Typography>
          <Grid container>
            <Grid item xs={12}>
              <FormControl className='form-control' >
                <RadioGroup
                  className='radio-group'
                  row
                  name="iv_applicant_status"
                  defaultValue={paymentPlan}
                  value={paymentPlan}
                  onChange={PaymentPlan}
                >
                  <FormControlLabel
                    value={1}
                    className='form-control-label'
                    control={<Radio />}
                    label="One time Payment"
                    name='onetimepayment'
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value={2}
                    className='form-control-label'
                    control={<Radio />}
                    label="3 Months Installments"
                    name='3monthsinstallmnents'
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value={3}
                    className='form-control-label'
                    control={<Radio />}
                    label="6 Months Installments"
                    name='onetimepayment'
                    labelPlacement="end"
                  />
                </RadioGroup>
              </FormControl>
              <Box className='from-Error'>
                {errors.iv_payment_plan && touched.iv_payment_plan ? (errors.iv_payment_plan) : null}
              </Box>
            </Grid>
          </Grid>
          <Typography variant="h6" className='groupTitle'>
            Applicant's Details
          </Typography>
          <Grid container >
            <Grid item xs={4}>
              <Typography className='fieldLabel' >Name</Typography>
              <TextField
                required
                type='text'
                name="iv_applicant_name"
                value={values.iv_applicant_name}
                autoComplete='off'
                onChange={handleChange}
                onBlur={handleBlur}
                id="iv_applicant_name"
                className='textInputField'
              />
              <Box className='from-Error'>
                {errors.iv_applicant_name && touched.iv_applicant_name ? (errors.iv_applicant_name) : null}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography className='fieldLabel'>NIC. / CNIC. / NICOP.</Typography>
              <TextField
                required
                type='text'
                name="iv_applicant_cnic"
                value={values.iv_applicant_cnic}
                onChange={handleChange}
                onBlur={handleBlur}
                id="iv_applicant_cnic"
                className='textInputField'
                inputProps={{ maxLength: 13, autoComplete: 'off' }}
              />
              <Box className='from-Error'>
                {errors.iv_applicant_cnic && touched.iv_applicant_cnic ? (errors.iv_applicant_cnic) : null}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography className='fieldLabel'  >Phone No.</Typography>
              <TextField
                required
                type='text'
                name="iv_applicant_phone_no"
                autoComplete='off'
                value={values.iv_applicant_phone_no}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{ maxLength: 11 }}
                id="iv_applicant_phone_no"
                className='textInputField'
              />
              <Box className='from-Error'>
                {errors.iv_applicant_phone_no && touched.iv_applicant_phone_no ? (errors.iv_applicant_phone_no) : null}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography className='fieldLabel'  >Email<span style={{ color: "#A1A1A1" }}>(Optional)</span></Typography>
              <TextField
                autoComplete='off'
                required
                type='text'
                name="iv_applicant_email"
                value={values.iv_applicant_email}
                onChange={handleChange}
                onBlur={handleBlur}
                id="iv_applicant_email"
                className='textInputField'
              />
              <Box className='from-Error'>
                {errors.iv_applicant_email && touched.iv_applicant_email ? (errors.iv_applicant_email) : null}
              </Box>
            </Grid>
          </Grid>
          <Typography variant="h6" className='groupTitle'>
            Required Documents
          </Typography>
          <Grid container spacing={1} className='documentsContainer' >
            <Grid item lg='2' >
              <Typography className='documentLabel' >1- Applicant's CNIC.</Typography>
              <Box className='documentError'>{errors.iv_applicant_cnic_document && touched.iv_applicant_cnic_document ? (errors.iv_applicant_cnic_document) : null}</Box>
            </Grid>
            <Grid item lg='2'>
              {
                applicantCnicDocument ?
                  <>
                    <Button varient='contained' component="label" startIcon={<DeleteOutlinedIcon sx={{ color: 'red' }} onClick={() => { setApplicantCnicDocument('') }} />}  >
                      {applicantCnicDocumentThumbnail && <img src={applicantCnicDocumentThumbnail} alt="Applicant CNIC" width="100" />}
                    </Button>
                  </>
                  :
                  <>
                    <Button variant="contained" component="label" startIcon={<FileUploadOutlinedIcon />}  >
                      Upload
                      <input hidden accept={documentExtensions} id='iv_applicant_cnic_document' name='iv_applicant_cnic_document' type="file" onChange={ApplicantCnicDocument} />
                    </Button>
                  </>
              }
            </Grid>
            <Grid item lg='2' >
              <Typography className='documentLabel' >2- Last Paid Bill</Typography>
              <Box className='documentError'>{errors.iv_last_paid_bill && touched.iv_last_paid_bill ? (errors.iv_last_paid_bill) : null}</Box>
            </Grid>
            <Grid item lg='2'>
              {
                lastPaidBill ?
                  <>
                    <Button varient='contained' component="label" startIcon={<DeleteOutlinedIcon sx={{ color: 'red' }} onClick={() => { setLastPaidBill('') }} />}  >
                      {lastPaidBillThumbnail && <img src={lastPaidBillThumbnail} alt="Last Paid Bill" width="100" />}
                    </Button>
                  </>
                  :
                  <>
                    <Button variant="contained" component="label" startIcon={<FileUploadOutlinedIcon />}  >
                      Upload
                      <input hidden accept={documentExtensions} id='iv_last_paid_bill' name='iv_last_paid_bill' type="file" onChange={LastPaidBill} />
                    </Button>
                  </>
              }
            </Grid>
          </Grid>
          <Box sx={{ marginTop: '1%', display: 'flex', flexDirection: 'row-reverse', marginRight: '1%' }}>
            <Button sx={{ width: '35%', marginLeft: '1%', marginBottom: '1%' }} variant="contained" component="label" type='submit' onClick={handleSubmit} disabled={isLoadingSpinner}>
              {isLoadingSpinner ? (
                <Box sx={{ display: 'flex' }}>
                  <CircularProgress />
                </Box>
              ) : (
                'Submit'
              )}
            </Button>

            < ToastContainer autoClose={5000} style={{ width: '50%' }} theme="colored" />
          </Box>
        </form >
      </Box >
    </Grid >
  );
}

export default OptForSmartMeterForm;